import i18n from '../i18n/i18n';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTrialStatus } from '../hooks/useTrialStatus';
import { useAppSelector, useAppDispatch } from '../redux/stateHooks';
import {
  selectCheckingExpiry,
  fetchCurrentSubscription,
  fetchMyTrialStatus,
} from '../redux/slices/subscriptionSliceModel';
import { APP_FEATURES } from '../constants';
import { hasPrivilege } from '../utility/hasPrivilege';

interface SubscriptionGuardProps {
  children: React.ReactNode;
}

// Routes that should bypass subscription checks completely
const BYPASS_ROUTES = [
  '/login',
  '/signup',
  '/forgot-password',
  '/reset-password',
  '/billing-and-plans',
  '/payment-jail',
  '/support',
  '/logout',
  '/etl-dashboard', // ETL setup must never be blocked — subscription is created AFTER ETL completes
  '/classveew-ai',
  '/home', // Common landing for zero-privilege roles (students/guardians) — never bounce to billing

  '/access-denied', // Non-billing users land here when org is restricted; no further redirects
];

const SubscriptionGuard: React.FC<SubscriptionGuardProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const [trialExpiredRedirected, setTrialExpiredRedirected] = useState(false);

  // ALL hooks must be called unconditionally before any early return (Rules of Hooks).

  // Fetch trial status (for trial expiry redirect)
  const { status: trialStatus } = useTrialStatus();
  const isCheckingExpiry = useAppSelector(selectCheckingExpiry);

  // Fetch currentSubscription on mount and whenever the user returns to this tab.
  // All authenticated org users fetch this — non-billing users get grace/trial info
  // for banners; billing users get the full subscription status for guard redirects.
  useEffect(() => {
    dispatch(fetchCurrentSubscription());

    const onFocus = () => {
      if (document.visibilityState === 'visible') dispatch(fetchCurrentSubscription());
    };
    window.addEventListener('visibilitychange', onFocus);
    return () => window.removeEventListener('visibilitychange', onFocus);
  }, [dispatch]);

  const loading = isCheckingExpiry;

  // Check if current route should bypass subscription checks
  const shouldBypass = BYPASS_ROUTES.some((route) => location.pathname.startsWith(route));

  // Check if trial has expired using the precise trialEndsAt timestamp
  const isTrialExpired = useMemo(() => {
    if (!trialStatus) return false;
    if (!trialStatus.isTrial) return false;
    // Use the actual end timestamp — trialDaysRemaining is a floored display value
    // and can be 0 while the trial is still active (last day)
    if (trialStatus.trialEndsAt) {
      return new Date(trialStatus.trialEndsAt).getTime() <= Date.now();
    }
    // No end date means trial never expires (shouldn't happen but be safe)
    return false;
  }, [trialStatus]);

  // Precision timer: when trialEndsAt is known, schedule an exact refresh at that moment
  // so the redirect fires immediately on expiry instead of waiting for the next poll.
  useEffect(() => {
    if (!trialStatus?.trialEndsAt || shouldBypass) return;
    const expiresAt = new Date(trialStatus.trialEndsAt).getTime();
    const delay = expiresAt - Date.now();
    if (delay <= 0) {
      // Already expired — force an immediate refresh
      dispatch(fetchCurrentSubscription());
      dispatch(fetchMyTrialStatus());
      return;
    }
    // Set a timer to fire exactly when the trial ends
    const timer = setTimeout(() => {
      dispatch(fetchCurrentSubscription());
      dispatch(fetchMyTrialStatus());
    }, delay);
    return () => clearTimeout(timer);
  }, [trialStatus?.trialEndsAt, shouldBypass, dispatch]);

  // Handle trial expiry redirect
  useEffect(() => {
    if (shouldBypass || loading || trialExpiredRedirected) {
      return;
    }

    // If trial has expired, redirect to billing-and-plans — only for users who
    // can act on it. Everyone else gets the lock screen (CVA-4668); sending a
    // teacher, parent or student to billing lands them on access-denied, the
    // dead end CVA-3661 was about.
    if (
      isTrialExpired &&
      hasPrivilege(APP_FEATURES.BILLING_MANAGEMENT) &&
      !location.pathname.startsWith('/billing-and-plans')
    ) {
      setTrialExpiredRedirected(true);
      navigate('/billing-and-plans', {
        state: {
          fromTrialExpiry: true,
          message: i18n.t(
            'msg.your_trial_has_expired_please_select_a_plan_to_c',
            'Your trial has expired. Please select a plan to continue.',
          ),
        },
        replace: true,
      });
    }
  }, [isTrialExpired, loading, shouldBypass, location.pathname, navigate, trialExpiredRedirected]);

  // Reset redirect flag when leaving billing-and-plans
  useEffect(() => {
    if (!location.pathname.startsWith('/billing-and-plans')) {
      setTrialExpiredRedirected(false);
    }
  }, [location.pathname]);

  // Handle payment restriction - DO NOT REDIRECT. Let the overlay handle user tracking.
  useEffect(() => {
    if (shouldBypass || loading) {
      return;
    }
    // We no longer trigger navigate() steps here for restrictions.
    // This stops non-billing users from getting bounced to the /access-denied route.
  }, [shouldBypass, loading]);

  // Active trial users bypass all guard restrictions — render children immediately.
  // This is a conditional render (not an early return) so all hooks above always run.
  if (trialStatus?.isTrial === true) {
    return <>{children}</>;
  }

  return <>{children}</>;
};

export default SubscriptionGuard;
